import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { formatTimeStamp } from '../utils/dates'
import { BaseRecord } from './BaseRecord'
import { Bookable, ScheduleState } from './Bookable'
import { Item } from './Item'
import { Mappable } from './Mappable'
import { Option } from './Option'
import { Person } from './Person'
import { FKType, SQLBuilder } from './SQLBuilder'
import { SQLiteFactory } from './SQLiteFactory'
import { Schedule } from './Schedule'
import { User } from './User'

type LessonConstructorArgs = ConstructorArgs<() => Lesson>

export class Lesson extends BaseRecord implements Bookable, Mappable {
    readonly recordType = 'lesson'

    readonly iBookable = true
    readonly iMappable = true

    static CLASS_CAPACITY = 100
    static CLASS_INSTANCES = 200

    date?: moment.Moment
    endDate?: moment.Moment
    duration?: number // Integer
    capacity?: number // Integer
    longitude?: number
    latitude?: number
    line1?: string
    line2?: string
    city?: string
    state?: string
    postalCode?: string
    countryCode?: string
    private _location?: string
    completedDate?: moment.Moment
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    constructor(data?: Partial<LessonConstructorArgs>, params?: { item?: Item }) {
        super(data)
        if (!data) {
            this.item = params?.item
            if (params?.item) {
                this.capacity = params.item.capacity
                this.duration = params.item.duration
                this.employee = params.item.employee
                this.line1 = params.item.line1
                this.line2 = params.item.line2
                this.city = params.item.city
                this.state = params.item.state
                this.postalCode = params.item.postalCode
            }
            this.creator = PSApplication.me()
            this.owner = this.creator.owner
            return
        }

        this.date = this.propertyFromTimestampArg(data.date)
        this.endDate = this.propertyFromTimestampArg(data.end_date)
        this.duration = this.propertyFromIntArg(data.duration)
        this.capacity = this.propertyFromIntArg(data.capacity)
        this._location = data.location ?? undefined
        this.line1 = data.line1 ?? undefined
        this.line2 = data.line2 ?? undefined
        this.city = data.city ?? undefined
        this.state = data.state ?? undefined
        this.postalCode = data.postal_code ?? undefined
        this.countryCode = data.country_code ?? undefined
        this.longitude = this.propertyFromNumberArg(data.longitude)
        this.latitude = this.propertyFromNumberArg(data.latitude)
        this.completedDate = this.propertyFromTimestampArg(data.completed_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<LessonConstructorArgs>): Lesson {
        return new Lesson({ ...this.serializeForClone(), ...overrides })
    }

    static initWithItem(item: Item): Lesson {
        return new Lesson(undefined, { item })
    }

    get confirmedDate(): moment.Moment | undefined {
        return this.cancelledDate !== undefined ? undefined : this.createdDate
    }

    get person(): User | undefined {
        return this.employee ?? this.owner
    }

    get location(): string | undefined {
        return Person.locationString(
            this.line1,
            this.line2,
            this.city,
            this.state,
            this.postalCode,
            this._location
        )
    }

    get title(): string | undefined {
        return this.item?.name
    }

    get shortTitle(): string | undefined {
        return this.title
    }

    get coordinate(): [number, number] | undefined {
        if (this.latitude !== undefined && this.longitude !== undefined)
            return [this.latitude, this.longitude]
        return undefined
    }

    get color(): string | undefined {
        return this.item?.color
    }

    get allowNotify(): boolean {
        return true
    }

    get hasSchedule(): boolean {
        return this.schedule !== undefined && this.schedule.createdDate !== undefined
    }

    get isRecurring(): boolean {
        return (
            this.schedule !== undefined &&
            this.schedule.interval !== undefined &&
            this.schedule.interval > 0
        )
    }

    get scheduleStates(): ScheduleState[] {
        const states: ScheduleState[] = []
        states.push(ScheduleState.Editable)
        states.push(ScheduleState.Cancelable)
        return states
    }

    get memo(): string {
        return this.item?.memo ?? ''
    }

    static intervalStrings(): Option[] {
        const intervals: Option[] = [
            new Option(undefined, { id: '', label: 'Single class' }),
            new Option(undefined, { id: '1', label: 'Every week' }),
            new Option(undefined, { id: '2', label: 'Every 2 weeks' }),
            new Option(undefined, { id: '3', label: 'Every 3 weeks' }),
            new Option(undefined, { id: '4', label: 'Every 4 weeks' }),
            new Option(undefined, { id: '1m', label: 'Every month' }),
            new Option(undefined, { id: '5', label: 'Every 5 weeks' }),
            new Option(undefined, { id: '6', label: 'Every 6 weeks' }),
            new Option(undefined, { id: '7', label: 'Every 7 weeks' }),
            new Option(undefined, { id: '8', label: 'Every 8 weeks' }),
            new Option(undefined, { id: '2m', label: 'Every 2 months' }),
            new Option(undefined, { id: '9', label: 'Every 9 weeks' }),
            new Option(undefined, { id: '10', label: 'Every 10 weeks' }),
            new Option(undefined, { id: '11', label: 'Every 11 weeks' }),
            new Option(undefined, { id: '12', label: 'Every 12 weeks' }),
            new Option(undefined, { id: '3m', label: 'Every 3 months' }),
        ]
        return intervals
    }

    async statusString(): Promise<string> {
        if (this.cancelledDate) return `Canceled ${this.cancelledDate.format('MMM D, YYYY')}`
        if (this.completedDate) return `Completed ${this.completedDate.format('MMM D, YYYY')}`
        if (this.date && moment().isAfter(this.date)) return `Past ${this.capacityString}`
        return await this.capacityString()
    }

    async capacityString(): Promise<string> {
        const booked = await this.numberOfBookings()
        const left = (this.capacity ?? 0) - booked
        return left <= 0 ? 'This class is full' : left === 1 ? '1 spot left' : `${left} spots left`
    }

    async capacityDisplayString(): Promise<string> {
        return `Capacity ${this.capacity} (${await this.capacityString()})`
    }

    async numberOfBookings(): Promise<number> {
        if (this.id) {
            const sql = new SQLBuilder()
            sql.addField('count(*) as ct')
            sql.addTable('bookings')
            sql.filterBy('lesson = ?', this.id)
            sql.filterBy('cancelledDate is NULL')
            sql.filterBy("status != 'waitListed'")
            const result = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
            return parseInt(result['ct'])
        }
        return 0
    }

    async fullBooked() {
        return !!this.id && this.capacity && this.capacity <= (await this.numberOfBookings())
    }

    // Bookable interface methods
    get allowJoin(): boolean {
        return false
    }

    get allowCheckin(): boolean {
        return false
    }

    get ownerIsMe(): boolean {
        return this.owner != undefined && this.owner.id === PSApplication.me().id
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.item) this.item = BaseRecord.fromSerializer(data.item, Item)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.employee) this.employee = BaseRecord.fromSerializer(data.employee, User)
        if (data.schedule) this.schedule = BaseRecord.fromSerializer(data.schedule, Schedule)
        if (data.date) this.date = moment(data.date)
        if (data.endDate) this.endDate = moment(data.endDate)
        if (data.completedDate) this.completedDate = moment(data.completedDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        items: ['item'],
        users: ['owner', 'creator', 'employee', 'client'],
        schedules: ['schedule'],
    }

    static dataType(): DataType {
        return 'lessons'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        if (this.date) dict['date'] = formatTimeStamp(this.date)
        dict['item'] = this.item?.id ?? ''
        dict['employee'] = this.employee?.id ?? ''
        dict['location'] = this.location
        dict['line1'] = this.line1 ?? ''
        dict['line2'] = this.line2 ?? ''
        dict['city'] = this.city ?? ''
        dict['state'] = this.state ?? ''
        dict['postal_code'] = this.postalCode ?? ''
        dict['country_code'] = this.countryCode ?? ''
        dict['duration'] = this.duration ?? ''
        dict['schedule'] = this.schedule?.id ?? ''
        dict['capacity'] = this.capacity ?? ''
        return dict
    }

    get item(): Item | undefined {
        return this.getForeignRecord('item', Item)
    }
    set item(item: Item | undefined) {
        this.setForeignRecord('item', item)
    }
    get schedule(): Schedule | undefined {
        return this.getForeignRecord('schedule', Schedule)
    }
    set schedule(schedule: Schedule | undefined) {
        this.setForeignRecord('schedule', schedule)
    }
    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get employee(): User | undefined {
        return this.getForeignRecord('employee', User)
    }
    set employee(employee: User | undefined) {
        this.setForeignRecord('employee', employee)
    }
}
